import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ReportsSectionCompact.css";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  getSuperAdminBreakEvenSensitivityConfig,
  getSuperAdminBreakEvenSensitivityFilters,
  runSuperAdminBreakEvenSensitivityReport,
  updateSuperAdminBreakEvenSensitivityConfig,
} from "../api/superadminBreakEvenSensitivityReport";
import { BTN_PRIMARY, FIELD_CLASS, LABEL_TEXT, MODAL_TEXT } from "../ui/modalStyles";

dayjs.extend(utc);
dayjs.extend(timezone);

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const HELP_SECTIONS = [
  {
    id: "what",
    title: "What this report does",
    description:
      "Shows break-even revenue targets (daily/weekly/monthly) and required client volume.",
    bullets: [
      "Use it for planning capacity and revenue coverage.",
      "Compare measured KPI inputs vs saved assumptions.",
    ],
    anchorId: "sa-break-even-snapshot",
  },
  {
    id: "calcs",
    title: "How the calculations work (simple)",
    description: "Core formulas used by the report.",
    bullets: [
      "Break-even revenue = fixed costs / contribution margin",
      "Contribution margin = 1 - variable cost %",
      "Required clients/day = break-even daily / avg ticket",
      "Capacity/day = tech count x hours/day x clients/hour/tech",
    ],
    anchorId: "sa-break-even-targets",
  },
  {
    id: "inputs",
    title: "Inputs & assumptions",
    description:
      "Inputs include avg ticket, clients/hour/tech, hours/day, active tech count, days open, variable cost %, and fixed monthly costs.",
    bullets: [
      "Avg ticket: measured or override",
      "Clients/hour/tech: measured or override",
      "Hours/day and active tech count: measured or override",
    ],
    anchorId: "sa-break-even-inputs",
  },
  {
    id: "confidence",
    title: "Confidence score",
    description:
      "Confidence improves when measured data coverage is stronger and fewer fallback assumptions are needed.",
    anchorId: "sa-break-even-snapshot",
  },
  {
    id: "best",
    title: "Best practices",
    description:
      "Save assumptions in Salon scope. Use Business/All for high-level planning and comparison.",
    anchorId: "sa-break-even-inputs",
  },
];

const FIELD_TOOLTIPS = {
  scope:
    "Choose all salons, one business, or one salon. Date logic uses salon/business timezone rules.",
  business: "Required in Business scope.",
  salon: "Required in Salon scope.",
  start_date: "First accounting date included in this report.",
  end_date: "Last accounting date included in this report.",
  read_only_assumptions:
    "Assumptions are stored per salon. Switch to Salon scope to edit and save.",
  avg_ticket_override: "If blank, measured average ticket is used when available.",
  clients_per_hour_per_tech_override:
    "If blank, measured throughput is used when available.",
  hours_per_day_override:
    "If blank, business hours or schedule-derived hours are used.",
  active_tech_count_override:
    "If blank, measured active/scheduled technician count is used.",
  variable_cost_percent:
    "Includes supplies, merchant fees, and other revenue-linked variable costs.",
  revenue_formula: "Break-even revenue = fixed cost / contribution margin.",
  required_formula: "Required clients = break-even revenue / average ticket.",
  per_tech_formula: "Required clients/day divided by active technicians.",
};

const DEFAULT_CONFIG = {
  rent_monthly: 0,
  receptionist_monthly: 0,
  manager_monthly: 0,
  other_fixed_monthly: 0,
  variable_cost_percent: 0,
  avg_ticket_override: "",
  clients_per_hour_per_tech_override: "",
  hours_per_day_override: "",
  days_open_per_week: 6,
  active_tech_count_override: "",
  avg_ticket_low: "",
  avg_ticket_high: "",
  clients_per_hour_low: "",
  clients_per_hour_high: "",
  tech_count_low: "",
  tech_count_high: "",
  hours_per_day_low: "",
  hours_per_day_high: "",
  notes: "",
};

const CARD_CLASS = "rounded-2xl border border-slate-200 bg-white p-5";

const sourceBadgeClass = (source) => {
  if (source === "measured") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (source === "override") return "bg-sky-100 text-sky-700 border-sky-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const sourceLabel = (source) => {
  if (source === "measured") return "Measured";
  if (source === "override") return "Assumed (Override)";
  return "Assumed";
};

const confidenceBadgeClass = (label) => {
  if (label === "High") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (label === "Medium") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-rose-100 text-rose-700 border-rose-200";
};

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  const abs = Math.abs(num).toFixed(2);
  return num < 0 ? `-$${abs}` : `$${abs}`;
};

const formatNumber = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  return num.toFixed(digits);
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumberOrUndefined = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const formatDropdownLabel = (entry, fallbackPrefix) => {
  const id = entry?.id;
  const name = entry?.name || fallbackPrefix;
  if (id === null || id === undefined || id === "") return name;
  return `${id} - ${name}`;
};

export default function SuperAdminBreakEvenSensitivityReport() {
  const { is_super_admin, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const hasAutoRunRef = useRef(false);

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [report, setReport] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [scenarioView, setScenarioView] = useState("core");
  const [scenarioSearch, setScenarioSearch] = useState("");

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((entry) => entry && map.set(String(entry.id), entry));
    return map;
  }, [businesses]);
  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((entry) => entry && map.set(String(entry.id), entry));
    return map;
  }, [salons]);
  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return (salons || []).filter(
      (entry) => String(entry.business_id || "") === String(businessId)
    );
  }, [salons, businessId]);

  const anchorTimezone = useMemo(() => {
    if (scope === "salon" && salonId) {
      return salonMap.get(String(salonId))?.timezone || authSalonTimezone || "UTC";
    }
    if (scope === "business" && businessId) {
      return businessMap.get(String(businessId))?.timezone || authSalonTimezone || "UTC";
    }
    return authSalonTimezone || "UTC";
  }, [scope, salonId, salonMap, businessId, businessMap, authSalonTimezone]);

  useEffect(() => {
    if (!anchorTimezone) return;
    const now = dayjs().tz(anchorTimezone);
    setEndDate(now.format("YYYY-MM-DD"));
    setStartDate(now.subtract(12, "month").add(1, "day").format("YYYY-MM-DD"));
    hasAutoRunRef.current = false;
  }, [anchorTimezone, scope]);

  useEffect(() => {
    if (!salonId || !businessId) return;
    const selectedSalon = salonMap.get(String(salonId));
    if (!selectedSalon || String(selectedSalon.business_id || "") !== String(businessId)) {
      setSalonId("");
    }
  }, [businessId, salonId, salonMap]);

  const loadFilters = useCallback(async () => {
    if (!is_super_admin) return;
    setFiltersLoading(true);
    setFiltersError("");
    try {
      const data = await getSuperAdminBreakEvenSensitivityFilters({
        scope,
        business_id: toNumberOrUndefined(businessId),
        salon_id: toNumberOrUndefined(salonId),
      });
      setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
      setSalons(Array.isArray(data?.salons) ? data.salons : []);
    } catch (err) {
      setBusinesses([]);
      setSalons([]);
      setFiltersError(err?.message || "Unable to load report filters.");
    } finally {
      setFiltersLoading(false);
    }
  }, [is_super_admin, scope, businessId, salonId]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const loadSalonConfig = useCallback(async () => {
    if (!is_super_admin || scope !== "salon" || !salonId) return;
    setLoadingConfig(true);
    setError("");
    try {
      const data = await getSuperAdminBreakEvenSensitivityConfig(Number(salonId));
      setConfig({ ...DEFAULT_CONFIG, ...(data || {}) });
    } catch (err) {
      setError(err?.message || "Unable to load break-even assumptions.");
    } finally {
      setLoadingConfig(false);
    }
  }, [is_super_admin, scope, salonId]);

  useEffect(() => {
    if (scope !== "salon") return;
    loadSalonConfig();
  }, [scope, salonId, loadSalonConfig]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "Start and end dates are required.";
    return endDate < startDate ? "End date cannot be before start date." : "";
  }, [startDate, endDate]);
  const canEditAssumptions = scope === "salon" && Boolean(salonId);
  const canRun = useMemo(() => {
    if (dateRangeError) return false;
    if (scope === "business" && !businessId) return false;
    if (scope === "salon" && !salonId) return false;
    return Boolean(startDate && endDate);
  }, [dateRangeError, scope, businessId, salonId, startDate, endDate]);

  const payload = useMemo(
    () => ({
      scope,
      business_id: toNumberOrUndefined(businessId),
      salon_id: toNumberOrUndefined(salonId),
      start_date: startDate,
      end_date: endDate,
    }),
    [scope, businessId, salonId, startDate, endDate]
  );

  const runReport = useCallback(async () => {
    if (!canRun) {
      setError(
        dateRangeError ||
          (scope === "business"
            ? "Choose a business for Business scope."
            : scope === "salon"
            ? "Choose a salon for Salon scope."
            : "Complete required filters.")
      );
      return;
    }
    setRunning(true);
    setError("");
    try {
      const data = await runSuperAdminBreakEvenSensitivityReport(payload);
      setReport(data || null);
      if (!canEditAssumptions) {
        setConfig((prev) => ({ ...DEFAULT_CONFIG, ...(prev || {}), ...(data?.assumptions_used || {}) }));
      }
    } catch (err) {
      setReport(null);
      setError(err?.message || "Unable to run break-even report.");
    } finally {
      setRunning(false);
    }
  }, [canRun, dateRangeError, scope, payload, canEditAssumptions]);

  useEffect(() => {
    if (!is_super_admin || hasAutoRunRef.current) return;
    if (filtersLoading) return;
    if (!canRun) return;
    if (scope === "salon" && loadingConfig) return;
    hasAutoRunRef.current = true;
    runReport();
  }, [is_super_admin, filtersLoading, canRun, scope, loadingConfig, runReport]);

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...(prev || {}), [field]: value }));
    setSaveMessage("");
  };

  const handleSave = async () => {
    if (!canEditAssumptions || !config) return;
    setSaving(true);
    setError("");
    setSaveMessage("");
    try {
      const savePayload = {
        rent_monthly: Number(config.rent_monthly || 0),
        receptionist_monthly: Number(config.receptionist_monthly || 0),
        manager_monthly: Number(config.manager_monthly || 0),
        other_fixed_monthly: Number(config.other_fixed_monthly || 0),
        variable_cost_percent: Number(config.variable_cost_percent || 0),
        avg_ticket_override: toOptionalNumber(config.avg_ticket_override),
        clients_per_hour_per_tech_override: toOptionalNumber(config.clients_per_hour_per_tech_override),
        hours_per_day_override: toOptionalNumber(config.hours_per_day_override),
        days_open_per_week: Math.min(7, Math.max(1, Math.floor(Number(config.days_open_per_week || 6)))),
        active_tech_count_override: toOptionalNumber(config.active_tech_count_override),
        avg_ticket_low: toOptionalNumber(config.avg_ticket_low),
        avg_ticket_high: toOptionalNumber(config.avg_ticket_high),
        clients_per_hour_low: toOptionalNumber(config.clients_per_hour_low),
        clients_per_hour_high: toOptionalNumber(config.clients_per_hour_high),
        tech_count_low: toOptionalNumber(config.tech_count_low),
        tech_count_high: toOptionalNumber(config.tech_count_high),
        hours_per_day_low: toOptionalNumber(config.hours_per_day_low),
        hours_per_day_high: toOptionalNumber(config.hours_per_day_high),
        notes: config.notes || null,
      };
      const saved = await updateSuperAdminBreakEvenSensitivityConfig(Number(salonId), savePayload);
      setConfig({ ...DEFAULT_CONFIG, ...(saved || savePayload) });
      setSaveMessage("Assumptions saved.");
      await runReport();
    } catch (err) {
      setError(err?.message || "Unable to save assumptions.");
    } finally {
      setSaving(false);
    }
  };

  const handleScopeChange = (value) => {
    setScope(value);
    setError("");
    hasAutoRunRef.current = false;
    if (value !== "salon") setSalonId("");
    if (value === "all") setBusinessId("");
  };
  const handleBusinessChange = (value) => {
    setBusinessId(value);
    setError("");
    hasAutoRunRef.current = false;
    if (!value && scope !== "salon") setSalonId("");
  };
  const handleSalonChange = (value) => {
    setSalonId(value);
    setError("");
    hasAutoRunRef.current = false;
    if (!value) return;
    const selectedSalon = salonMap.get(String(value));
    if (selectedSalon?.business_id) setBusinessId(String(selectedSalon.business_id));
  };

  const measured = report?.measured_inputs || {};
  const assumptions = report?.assumptions_used || {};
  const breakEven = report?.break_even || {};
  const requiredVolume = report?.required_volume || {};
  const capacity = report?.capacity || {};
  const confidence = report?.confidence || {};
  const scenarios = useMemo(
    () => (Array.isArray(report?.sensitivity?.scenarios) ? report.sensitivity.scenarios : []),
    [report?.sensitivity?.scenarios]
  );
  const recommendations = Array.isArray(report?.recommendations) ? report.recommendations : [];

  const visibleScenarios = useMemo(() => {
    const search = scenarioSearch.trim().toLowerCase();
    return scenarios.filter((row) => {
      if (scenarioView === "core" && row.scenario_type !== "core") return false;
      if (!search) return true;
      return String(row.label || "").toLowerCase().includes(search);
    });
  }, [scenarios, scenarioSearch, scenarioView]);

  const confidenceScore = Number(confidence?.score || 0);
  const utilizationPct = Number(capacity?.utilization_needed) * 100;
  const feasibilityLabel = capacity?.feasible ? "Feasible" : "Not feasible";

  const timezoneLine = useMemo(() => {
    if (scope === "salon") {
      const tz = report?.date_range?.tz_name || salonMap.get(String(salonId))?.timezone || anchorTimezone;
      return `Timezone: ${tz || "UTC"}`;
    }
    if (scope === "business") {
      const tz = report?.date_range?.tz_name || businessMap.get(String(businessId))?.timezone || anchorTimezone;
      return `Timezone: ${tz || "UTC"}`;
    }
    if ((report?.date_range?.tz_name || "").toLowerCase() === "per-salon") {
      return "Timezone: per-salon local accounting day (each salon timezone).";
    }
    return `Timezone: ${report?.date_range?.tz_name || anchorTimezone || "UTC"}`;
  }, [scope, report?.date_range?.tz_name, salonMap, salonId, businessMap, businessId, anchorTimezone]);

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? <InfoTooltip text={FIELD_TOOLTIPS[tooltipKey]} label={label} /> : null}
    </div>
  );

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader
            title="Break-Even Point Sensitivity Analysis"
            closeTo="/superadmin/reports"
          />

          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <ReportHelpCard
              title="Help Guide: Break-Even Point Sensitivity Analysis"
              subtitle="Estimate break-even targets and review scenario sensitivity across Super Admin scope."
              sections={HELP_SECTIONS}
              onJumpToSection={jumpToSectionWithHighlight}
            />

            <div className={`${CARD_CLASS} space-y-4`}>
              <div className="grid gap-4 lg:grid-cols-6">
                <div>
                  {renderFieldLabel("Scope", "scope")}
                  <select
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={scope}
                    onChange={(e) => handleScopeChange(e.target.value)}
                    disabled={filtersLoading}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Business", "business")}
                  <select
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={businessId}
                    onChange={(e) => handleBusinessChange(e.target.value)}
                    disabled={filtersLoading || !(scope === "business" || scope === "salon")}
                  >
                    <option value="">Select business</option>
                    {businesses.map((entry) => (
                      <option key={entry.id} value={String(entry.id)}>
                        {formatDropdownLabel(entry, "Business")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Salon", "salon")}
                  <select
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={salonId}
                    onChange={(e) => handleSalonChange(e.target.value)}
                    disabled={filtersLoading || scope !== "salon"}
                  >
                    <option value="">Select salon</option>
                    {filteredSalons.map((entry) => (
                      <option key={entry.id} value={String(entry.id)}>
                        {formatDropdownLabel(entry, "Salon")}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setError("");
                      hasAutoRunRef.current = false;
                    }}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    className={`w-full mt-1 ${FIELD_CLASS}`}
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setError("");
                      hasAutoRunRef.current = false;
                    }}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    className={`w-full ${BTN_PRIMARY}`}
                    onClick={runReport}
                    disabled={running || filtersLoading || !canRun}
                  >
                    {running ? "Running..." : "Run Report"}
                  </button>
                </div>
              </div>
              <div className="text-sm text-slate-500">{timezoneLine}</div>
            </div>

            {filtersError ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {filtersError}
              </div>
            ) : null}
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            ) : null}
            {saveMessage ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {saveMessage}
              </div>
            ) : null}

            <div
              id="sa-break-even-snapshot"
              className={`${CARD_CLASS} ${getSectionClassName("sa-break-even-snapshot")}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Snapshot</h2>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${confidenceBadgeClass(
                      confidence?.label
                    )}`}
                  >
                    {confidence?.label || "Low"} confidence
                  </span>
                  <span
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                      capacity?.feasible
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-rose-200 bg-rose-50 text-rose-700"
                    }`}
                  >
                    {feasibilityLabel}
                  </span>
                </div>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Break-even Daily
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(breakEven.daily_revenue)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Break-even Weekly
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(breakEven.weekly_revenue)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Break-even Monthly
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(breakEven.monthly_revenue)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Confidence Score
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatNumber(confidenceScore, 0)}
                  </div>
                </div>
              </div>
            </div>

            <div
              id="sa-break-even-inputs"
              className={`${CARD_CLASS} ${getSectionClassName("sa-break-even-inputs")} space-y-4`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Inputs & Assumptions
                </h2>
                <div className="flex items-center gap-2">
                  {!canEditAssumptions ? (
                    <InfoTooltip text={FIELD_TOOLTIPS.read_only_assumptions} label="Assumptions" />
                  ) : null}
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!canEditAssumptions || saving || loadingConfig || !config}
                    className={BTN_PRIMARY}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
              {!canEditAssumptions ? (
                <p className="text-sm text-slate-500">
                  Assumptions are stored per salon. Switch to{" "}
                  <span className="font-semibold">Salon</span> scope to edit and save.
                </p>
              ) : null}
              {loadingConfig ? (
                <div className="text-slate-500">Loading assumptions...</div>
              ) : (
                <>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {[
                      [
                        "avg_ticket_override",
                        "Avg ticket override",
                        measured.avg_ticket,
                        assumptions.avg_ticket_source,
                      ],
                      [
                        "clients_per_hour_per_tech_override",
                        "Clients/hour/tech override",
                        measured.clients_per_hour_per_tech,
                        assumptions.clients_per_hour_per_tech_source,
                      ],
                      [
                        "hours_per_day_override",
                        "Hours/day override",
                        measured.hours_per_day,
                        assumptions.hours_per_day_source,
                      ],
                      [
                        "active_tech_count_override",
                        "Active tech count override",
                        measured.active_tech_count,
                        assumptions.active_tech_count_source,
                      ],
                    ].map(([key, label, measuredValue, source]) => (
                      <div key={String(key)} className="rounded-xl border border-slate-200 p-3">
                        <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                          {label}
                          <InfoTooltip text={FIELD_TOOLTIPS[String(key)]} label={String(label)} />
                        </div>
                        <input
                          type="number"
                          className={`w-full mt-2 ${FIELD_CLASS}`}
                          value={config?.[String(key)] ?? ""}
                          onChange={(e) => updateField(String(key), e.target.value)}
                          disabled={!canEditAssumptions}
                        />
                        <div className="mt-2 text-xs text-slate-500">
                          Measured:{" "}
                          {String(key).includes("ticket")
                            ? formatMoney(measuredValue)
                            : formatNumber(measuredValue)}
                        </div>
                        <span
                          className={`mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${sourceBadgeClass(
                            source
                          )}`}
                        >
                          {sourceLabel(source)}
                        </span>
                      </div>
                    ))}
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="text-xs uppercase tracking-wide text-slate-500">
                        Days open per week
                      </div>
                      <input
                        type="number"
                        min="1"
                        max="7"
                        className={`w-full mt-2 ${FIELD_CLASS}`}
                        value={config.days_open_per_week ?? 6}
                        onChange={(e) => updateField("days_open_per_week", e.target.value)}
                        disabled={!canEditAssumptions}
                      />
                    </div>
                    <div className="rounded-xl border border-slate-200 p-3">
                      <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                        Variable cost %
                        <InfoTooltip
                          text={FIELD_TOOLTIPS.variable_cost_percent}
                          label="Variable cost percent"
                        />
                      </div>
                      <input
                        type="number"
                        className={`w-full mt-2 ${FIELD_CLASS}`}
                        value={config.variable_cost_percent ?? 0}
                        onChange={(e) => updateField("variable_cost_percent", e.target.value)}
                        disabled={!canEditAssumptions}
                      />
                    </div>
                    {[
                      ["rent_monthly", "Rent (monthly)"],
                      ["receptionist_monthly", "Receptionist (monthly)"],
                      ["manager_monthly", "Manager (monthly)"],
                      ["other_fixed_monthly", "Other fixed (monthly)"],
                    ].map(([key, label]) => (
                      <div key={key} className="rounded-xl border border-slate-200 p-3">
                        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                        <input
                          type="number"
                          className={`w-full mt-2 ${FIELD_CLASS}`}
                          value={config[key] ?? 0}
                          onChange={(e) => updateField(key, e.target.value)}
                          disabled={!canEditAssumptions}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">
                        Advanced Sensitivity Ranges
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced((prev) => !prev)}
                        className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        {showAdvanced ? "Hide" : "Show"}
                      </button>
                    </div>
                    {showAdvanced ? (
                      <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                        {[
                          ["avg_ticket_low", "Avg ticket low"],
                          ["avg_ticket_high", "Avg ticket high"],
                          ["clients_per_hour_low", "Clients/hour low"],
                          ["clients_per_hour_high", "Clients/hour high"],
                          ["tech_count_low", "Tech count low"],
                          ["tech_count_high", "Tech count high"],
                          ["hours_per_day_low", "Hours/day low"],
                          ["hours_per_day_high", "Hours/day high"],
                        ].map(([key, label]) => (
                          <label key={key} className="text-sm text-slate-700">
                            {label}
                            <input
                              type="number"
                              className={`w-full mt-1 ${FIELD_CLASS}`}
                              value={config[key] ?? ""}
                              onChange={(e) => updateField(key, e.target.value)}
                              disabled={!canEditAssumptions}
                            />
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            <div
              id="sa-break-even-targets"
              className={`${CARD_CLASS} ${getSectionClassName("sa-break-even-targets")}`}
            >
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">
                Break-even Targets & Required Volume
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    Revenue targets
                    <InfoTooltip text={FIELD_TOOLTIPS.revenue_formula} label="Revenue targets" />
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    <div>
                      Daily: <span className="font-semibold text-slate-900">{formatMoney(breakEven.daily_revenue)}</span>
                    </div>
                    <div>
                      Weekly: <span className="font-semibold text-slate-900">{formatMoney(breakEven.weekly_revenue)}</span>
                    </div>
                    <div>
                      Monthly: <span className="font-semibold text-slate-900">{formatMoney(breakEven.monthly_revenue)}</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    Required clients
                    <InfoTooltip text={FIELD_TOOLTIPS.required_formula} label="Required clients" />
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    <div>
                      Per day: <span className="font-semibold text-slate-900">{formatNumber(requiredVolume.clients_per_day)}</span>
                    </div>
                    <div>
                      Per week: <span className="font-semibold text-slate-900">{formatNumber(requiredVolume.clients_per_week)}</span>
                    </div>
                    <div>
                      Per month: <span className="font-semibold text-slate-900">{formatNumber(requiredVolume.clients_per_month)}</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    Clients per tech/day
                    <InfoTooltip text={FIELD_TOOLTIPS.per_tech_formula} label="Clients per tech/day" />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatNumber(requiredVolume.clients_per_tech_per_day)}
                  </div>
                </div>
              </div>
            </div>

            <div className={CARD_CLASS}>
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4">Capacity Check</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Capacity clients/day</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatNumber(capacity.capacity_clients_per_day)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Utilization needed</div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {Number.isFinite(utilizationPct) ? `${utilizationPct.toFixed(2)}%` : "Not enough data yet"}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Feasibility</div>
                  <div
                    className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                      capacity?.feasible
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-rose-200 bg-rose-50 text-rose-700"
                    }`}
                  >
                    {feasibilityLabel}
                  </div>
                </div>
              </div>
            </div>

            <div className={CARD_CLASS}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Sensitivity Scenarios
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className={`${FIELD_CLASS} h-10 text-sm`}
                    value={scenarioView}
                    onChange={(e) => setScenarioView(e.target.value)}
                  >
                    <option value="core">Core scenarios</option>
                    <option value="all">All scenarios</option>
                  </select>
                  <input
                    type="text"
                    value={scenarioSearch}
                    onChange={(e) => setScenarioSearch(e.target.value)}
                    placeholder="Search label"
                    className="h-10 rounded-xl border border-slate-300 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  />
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left">Scenario</th>
                      <th className="px-3 py-2 text-right">Avg ticket</th>
                      <th className="px-3 py-2 text-right">Clients/hr/tech</th>
                      <th className="px-3 py-2 text-right">Tech count</th>
                      <th className="px-3 py-2 text-right">Hours/day</th>
                      <th className="px-3 py-2 text-right">Break-even daily</th>
                      <th className="px-3 py-2 text-right">Required clients/day</th>
                      <th className="px-3 py-2 text-right">Utilization needed</th>
                      <th className="px-3 py-2 text-right">Feasible</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {running ? (
                      <tr>
                        <td className="px-3 py-4 text-slate-500" colSpan={9}>
                          Running projection...
                        </td>
                      </tr>
                    ) : null}
                    {!running && visibleScenarios.length === 0 ? (
                      <tr>
                        <td className="px-3 py-4 text-slate-500" colSpan={9}>
                          No scenarios found.
                        </td>
                      </tr>
                    ) : null}
                    {!running &&
                      visibleScenarios.map((row) => (
                        <tr key={`${row.label}-${row.scenario_type}`} className="hover:bg-slate-50">
                          <td className="px-3 py-2.5 font-semibold text-slate-900">{row.label}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{formatMoney(row.avg_ticket)}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{formatNumber(row.clients_per_hour)}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{formatNumber(row.tech_count, 0)}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{formatNumber(row.hours_per_day)}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{formatMoney(row.break_even_daily_revenue)}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">{formatNumber(row.required_clients_per_day)}</td>
                          <td className="px-3 py-2.5 text-right text-slate-700">
                            {Number.isFinite(Number(row.utilization_needed))
                              ? `${(Number(row.utilization_needed) * 100).toFixed(2)}%`
                              : "Not enough data yet"}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                                row.feasibility_flag
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-rose-200 bg-rose-50 text-rose-700"
                              }`}
                            >
                              {row.feasibility_flag ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={CARD_CLASS}>
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-3">
                Improve Your Break-Even
              </h2>
              <ul className="space-y-2">
                {recommendations.length === 0 ? (
                  <li className="text-slate-500">No recommendation signals yet.</li>
                ) : null}
                {recommendations.map((item, idx) => (
                  <li key={`${item.title}-${idx}`} className="rounded-xl border border-slate-200 p-3">
                    <div className="font-semibold text-slate-900">{item.title}</div>
                    <div className="text-sm text-slate-600 mt-1">{item.impact_hint}</div>
                    <div className="mt-2 text-xs text-slate-600">
                      <span className="rounded-full bg-slate-100 px-2 py-1">
                        Current: {item.current || "N/A"}
                      </span>
                      <span className="ml-2 rounded-full bg-rose-50 px-2 py-1 text-rose-700">
                        Target: {item.target || "N/A"}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
